import {IRestService} from "./IRestService";
import {IAssemblyService} from "./IAssemblyService";
import LzString from "lz-string";
import { AuthenticationService } from "./AuthenticationService";
import {
    normalizeAssemblyWritePayload,
    normalizeAssemblyWritePayloadArray,
    normalizeSubAssemblyWritePayload,
    normalizeSubAssemblyWritePayloadArray
} from "../helpers/apiEntityPayloads";

export class AssemblyService implements IAssemblyService {
    public constructor(private readonly restService: IRestService) {
    }

    public async getAssemblyById(assemblyId: string): Promise<any> {
        return this.restService.get(`assemblies/${assemblyId}`);
    }

    public async getSubAssemblyById(assemblyId: string): Promise<any> {
        return this.restService.get(`assemblies/subAssembly/${assemblyId}`);
    }

    public async getAssemblyByRef(assemblyId: string): Promise<any> {
        return this.restService.get(`assemblies/reference/${assemblyId}`);
    }

    public async getSubAssemblyByRef(assemblyId: string): Promise<any> {
        return this.restService.get(`assemblies/subAssembly/reference/${assemblyId}`);
    }

    public async getAllAssemblies(): Promise<any[]> {
        return this.restService.get(`assemblies`);
    }

    public async getAllAssembliesExpanded(): Promise<any[]> {
        const storageUpdated = localStorage.getItem('assembliesUpdated');
        const cachedCompressed = localStorage.getItem('assemblies');
        if (!storageUpdated || storageUpdated === '' || !cachedCompressed) {
            return this.restService.get(`assemblies/expanded`).then(async (assemblies: any[]) => {
                localStorage.setItem('assemblies', LzString.compress(JSON.stringify(assemblies)));
                const updatedOn = await this.getTableUpdatedDate('Assemblies');
                localStorage.setItem('assembliesUpdated', updatedOn);
                return assemblies;
            });
        }
        return this.getTableUpdatedDate('Assemblies').then((updatedOn) => {
            if (Date.parse(updatedOn) > Date.parse(storageUpdated)) {
                return this.restService.get(`assemblies/expanded`).then((assemblies: any[]) => {
                    localStorage.setItem('assemblies', LzString.compress(JSON.stringify(assemblies)));
                    localStorage.setItem('assembliesUpdated', updatedOn);
                    return assemblies;
                });
            } else {
                return JSON.parse(LzString.decompress(localStorage.getItem('assemblies')));
            }
        });
    }

    public async getAllSubAssembliesExpanded(): Promise<any[]> {
        const storageUpdated = localStorage.getItem('subAssembliesUpdated');
        const cachedCompressed = localStorage.getItem('subAssemblies');
        if (!storageUpdated || storageUpdated === '' || !cachedCompressed) {
            return this.restService.get(`assemblies/subassemblies/expanded`).then(async (assemblies: any[]) => {
                localStorage.setItem('subAssemblies', LzString.compress(JSON.stringify(assemblies)));
                const updatedOn = await this.getTableUpdatedDate('SubAssemblies');
                localStorage.setItem('subAssembliesUpdated', updatedOn);
                return assemblies;
            });
        }
        return this.getTableUpdatedDate('SubAssemblies').then((updatedOn) => {
            if (Date.parse(updatedOn) > Date.parse(storageUpdated)) {
                return this.restService.get(`assemblies/subassemblies/expanded`).then((assemblies: any[]) => {
                    localStorage.setItem('subAssemblies', LzString.compress(JSON.stringify(assemblies)));
                    localStorage.setItem('subAssembliesUpdated', updatedOn);
                    return assemblies;
                });
            } else {
                return JSON.parse(LzString.decompress(localStorage.getItem('subAssemblies')));
            }
        });
    }

    public async createAssembly(assembly: any): Promise<any> {
        const result = await this.restService.post(`assemblies/assembly`, normalizeAssemblyWritePayload(assembly));
        
        return result;
    }

    public async createSubAssembly(subAssembly: any): Promise<any> {
        const result = await this.restService.post(`assemblies/subAssembly`, normalizeSubAssemblyWritePayload(subAssembly));
        
        return result;
    }

    public async updateAssembly(assembly: any): Promise<any> {
        const result = await this.restService.put(`assemblies/assembly`, normalizeAssemblyWritePayload(assembly));
        
        return result;
    }

    public async updateAssemblyNoCache(assembly: any): Promise<any> {
        const result = await this.restService.put(`assemblies/assembly`, normalizeAssemblyWritePayload(assembly));
        
        return result;
    }

    public async uploadAssemblyImage(assemblyId: string, file: File): Promise<any> {
        const formData = new FormData();
        formData.append('file', file);
        
        return this.restService.post(`assemblies/assembly/${assemblyId}/upload-image`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        });
    }

    public async deleteAssembly(id: string): Promise<any> {
        const result = await this.restService.delete(`assemblies/assembly/${id}`);
        
        return result;
    }

    public async updateSubAssembly(subAssembly: any): Promise<any> {
        const result = await this.restService.put(`assemblies/subAssembly`, normalizeSubAssemblyWritePayload(subAssembly));
        
        return result;
    }

    public async updateSubAssemblyNoCache(subAssembly: any): Promise<any> {
        const result = await this.restService.put(`assemblies/subAssembly`, normalizeSubAssemblyWritePayload(subAssembly));
        
        return result;
    }

    public async uploadSubAssemblyImage(subAssemblyId: string, file: File): Promise<any> {
        const formData = new FormData();
        formData.append('file', file);
        
        return this.restService.post(`assemblies/subAssembly/${subAssemblyId}/upload-image`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        });
    }

    public async uploadAssemblyFile(assemblyId: string, file: File): Promise<any> {
        const formData = new FormData();
        formData.append('file', file);
        
        return this.restService.post(`assemblies/assembly/${assemblyId}/upload-file`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        });
    }

    public async uploadSubAssemblyFile(subAssemblyId: string, file: File): Promise<any> {
        const formData = new FormData();
        formData.append('file', file);
        
        return this.restService.post(`assemblies/subAssembly/${subAssemblyId}/upload-file`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        });
    }

    public async deleteSubAssembly(id: string): Promise<any> {
        const result = await this.restService.delete(`assemblies/subAssembly/${id}`);
        
        return result;
    }

    public async connectSubAssemblyToAssembly(subAssemblyId: string, assemblyId: string, quantity: string, bomRequired: boolean, order?: number): Promise<any> {
        let url = `assemblies/${subAssemblyId}/assembly/${assemblyId}/${quantity}/${bomRequired.toString()}`;
        if (order !== undefined) {
            url += `?order=${order}`;
        }
        return this.restService.post(url);
    }

    public async severSubAssemblyToAssembly(subAssemblyId: string, assemblyId: string): Promise<any> {
        return this.restService.delete(`assemblies/${subAssemblyId}/assembly/${assemblyId}`);
    }

    public async updateSubAssemblyToAssembly(subAssemblyToAssembly: any): Promise<any> {
        return this.restService.put(`assemblies/subassembly-to-assembly`, subAssemblyToAssembly);
    }

    public async connectSubAssemblyToSubAssembly(childSubAssemblyId: string, parentSubAssemblyId: string, quantity: string, bomRequired: boolean, order?: number): Promise<any> {
        let url = `assemblies/${childSubAssemblyId}/subassembly/${parentSubAssemblyId}/${quantity}/${bomRequired.toString()}`;
        if (order !== undefined) {
            url += `?order=${order}`;
        }
        return this.restService.post(url);
    }

    public async severSubAssemblyToSubAssembly(childSubAssemblyId: string, parentSubAssemblyId: string): Promise<any> {
        return this.restService.delete(`assemblies/${childSubAssemblyId}/subassembly/${parentSubAssemblyId}`);
    }

    public async deleteSubAssemblyToAssemblyConnection(connectionId: string): Promise<any> {
        return this.restService.delete(`assemblies/subassembly-to-assembly/${connectionId}`);
    }

    public async deleteSubAssemblyToSubAssemblyConnection(connectionId: string): Promise<any> {
        return this.restService.delete(`assemblies/subassembly-to-subassembly/${connectionId}`);
    }

    public async updateSubAssemblyToSubAssembly(subAssemblyToSubAssembly: any): Promise<any> {
        return this.restService.put(`assemblies/subassembly-to-subassembly`, subAssemblyToSubAssembly);
    }

    public async getPackageLogs(): Promise<any[]> {
        return this.restService.get(`assemblies/package-logs`);
    }

    public async createPackageLog(packageLog: any): Promise<any> {
        const result = this.restService.post(`assemblies/package-log`, packageLog);

        return result;
    }

    public async updatePackageLog(packageLog: any): Promise<any> {
        const result = this.restService.put(`assemblies/package-log`, packageLog);

        return result;
    }

    public async deletePackageLog(packageLog: any): Promise<any> {
        const result = this.restService.delete(`assemblies/package-log/${packageLog.id}`);

        return result;
    }

    public async getSalesLogs(): Promise<any[]> {
        return this.restService.get(`assemblies/sales-logs`);
    }

    public async getSalesLogsNoCache(): Promise<any[]> {
        const timestamp = Date.now();
        return this.restService.get(`assemblies/sales-logs?t=${timestamp}`);
    }

    public async getSalesLogSources(): Promise<any[]> {
        return this.restService.get(`assemblies/sales-log-sources`);
    }

    public async importSalesLogs(salesLogs: any[]): Promise<any[]> {
        const userId = AuthenticationService.getInstance().getUserId();
        salesLogs.forEach((salesLog) => {
            salesLog.userId = userId;
        });
        const result = await this.restService.post(`assemblies/sales-logs`, salesLogs);
        
        return result as any[];
    }

    public async createSalesLog(salesLog: any): Promise<any> {
        const userId = AuthenticationService.getInstance().getUserId();
        salesLog.userId = userId;
        const result = await this.restService.post(`assemblies/sales-log`, salesLog);
        
        return result as any;
    }

    public async createManualSalesLogs(salesLogs: any[]): Promise<any[]> {
        const userId = AuthenticationService.getInstance().getUserId();
        salesLogs.forEach((salesLog) => {
            salesLog.userId = userId;
        });
        const result = await this.restService.post(`assemblies/sales-logs-manual`, salesLogs);
        
        return result as any[];
    }

    public async updateSalesLog(salesLog: any): Promise<any> {
        const result = this.restService.put(`assemblies/sales-log/${salesLog.id}`, salesLog);
        return result;
    }

    public async deleteSalesLog(salesLog: any): Promise<any> {
        const result = this.restService.delete(`assemblies/sales-log/${salesLog.id}`);
        return result;
    }

    public getDiscountStrategies(): Promise<any[]> {
        return this.restService.get(`assemblies/discount-strategies`);
    }

    public createDiscountStrategy(discountStrategy: any): Promise<any> {
        return this.restService.post(`assemblies/discount-strategy`, discountStrategy);
    }

    public updateDiscountStrategy(discountStrategy: any): Promise<any> {
        return this.restService.put(`assemblies/discount-strategy`, discountStrategy);
    }

    public getAssemblyFamilies(): Promise<any[]> {
        return this.restService.get(`assemblies/assembly-families`);
    }

    public createAssemblyFamily(assemblyFamily: any): Promise<any> {
        return this.restService.post(`assemblies/assembly-family`, assemblyFamily);
    }

    public importAssemblies(assemblies: any[]): Promise<any[]> {
        return this.restService.post(`assemblies/import/assemblies`, normalizeAssemblyWritePayloadArray(assemblies));
    }

    public importSubAssemblies(subAssemblies: any[]): Promise<any[]> {
        return this.restService.post(`assemblies/import/subAssemblies`, normalizeSubAssemblyWritePayloadArray(subAssemblies));
    }

    public importPartsToAssemblies(parentChildren: any[]): Promise<any[]> {
        return this.restService.post(`assemblies/import/p-to-a`, parentChildren);
    }

    public importSubAssembliesToAssemblies(parentChildren: any[]): Promise<any[]> {
        return this.restService.post(`assemblies/import/sa-to-a`, parentChildren);
    }

    public importSubAssembliesToSubAssemblies(parentChildren: any[]): Promise<any[]> {
        return this.restService.post(`assemblies/import/sa-to-sa`, parentChildren);
    }

    public importPartsToSubAssemblies(parentChildren: any[]): Promise<any[]> {
        return this.restService.post(`assemblies/import/p-to-sa`, parentChildren);
    }

    public importPackagingLogs(importedLogs: any[]): Promise<any[]> {
        return this.restService.post(`assemblies/import/packaging`, importedLogs);
    }

    public getTodaysChanges(): Promise<any> {
        return this.restService.get(`assemblies/todays-changes`);
    }

    public updateAssemblyType(newValue: string, oldValue: string): Promise<any> {
        return this.restService.put(`assemblies/assembly-type`, {newValue: newValue, oldValue: oldValue});
    }

    public updateManufacturerFor(newValue: string, oldValue: string): Promise<any> {
        return this.restService.put(`assemblies/manufacturer-for`, {newValue: newValue, oldValue: oldValue});
    }

    public updateTertiaryCategory(newValue: string, oldValue: string): Promise<any> {
        return this.restService.put(`assemblies/tertiary-type`, {newValue: newValue, oldValue: oldValue});
    }

    public updateBrand(newValue: string, oldValue: string): Promise<any> {
        return this.restService.put(`assemblies/brand`, {newValue: newValue, oldValue: oldValue});
    }

    public updateAssemblyFamily(assemblyFamily: any): Promise<any> {
        return this.restService.put(`assemblies/assembly-family`, assemblyFamily);
    }

    public deleteAssemblyFamily(assemblyFamilyId: string): Promise<any> {
        return this.restService.delete(`assemblies/assembly-family/${assemblyFamilyId}`);
    }

    public updateConnectionOrders(updates: any[]): Promise<any> {
        return this.restService.put(`assemblies/update-connection-orders`, updates);
    }

    private getTableUpdatedDate(tableName: string): Promise<string> {
        return this.restService.get(`auth/${tableName}/check-updated`);
    }
}
